import * as stateMap from '../data/states-10m.json'
import type { Organization } from '../../../types'
import {
  InternMap,
  ScalePower,
  //descending,
  geoAlbersUsa,
  geoPath,
  select,
  zoom,
  zoomIdentity,
  forceCollide,
  forceSimulation,
  forceManyBody,
  forceX,
  forceY,
} from 'd3'
import { feature, feature as geoJSONFeature, mesh } from 'topojson-client'
import {
  type PropsWithChildren,
  useEffect,
  MutableRefObject,
  useState,
} from 'react'
import resetMapIcon from '../images/reset-map-icon.svg'
import ZoomComponent from './ZoomComponent'
import MapTooltip from './MapTooltip'
import { UseMeasureRef } from 'react-use/lib/useMeasure'
//import { Color } from '../../IntroSection/models'

type SlippyMapProps = PropsWithChildren<{
  display: string
  data: InternMap<string, Organization[]>
  rScale: ScalePower<number, number, never>
  onOrganizationClick: (
    organization: Organization | null,
    organizations: Organization[]
  ) => void
  svgMapRef: MutableRefObject<null>
  hiddenMapRef: UseMeasureRef<HTMLDivElement>
  mapWidth: number
  mapHeight: number
}>

interface OrganizationNode extends Organization {
  color: string | undefined
  x: number
  y: number
}

const DEFAULT_SCALE_EXTENT: [number, number] = [1, 32]

// const Colors: { [key: string]: string } = {
//   local: 'blue',
//   state: 'lightyellow',
//   regional: 'orange',
//   national: 'red',
//   global: 'ink',
// }

function SlippyMap({
  display,
  data,
  rScale,
  children,
  onOrganizationClick,
  svgMapRef,
  mapWidth,
  mapHeight,
  hiddenMapRef,
}: SlippyMapProps) {
  const handleOrganizationClick = (
    organization: Organization
    // extraOrganizations: Organization[]
  ) => {
    const city = organization.city
    const extraOrganizations = Array.from(data)
      .filter(([key]) => key === city)
      .map(([, values]) => values)
      .flat()

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'map_org_click', {
        org_name: organization.org_name,
        org_type: organization.initCommTask ? 'initiative' : 'organization',
        org_city: organization.city,
        org_state: organization.state,
      })
    }

    onOrganizationClick(organization, extraOrganizations)
  }

  //@ts-ignore
  const us = stateMap.default

  const geojson = geoJSONFeature(us, us.objects.states)
  const projection = geoAlbersUsa().fitSize([mapWidth, mapHeight], geojson)
  const path = geoPath().projection(projection)

  const [zoomLevel, setZoomLevel] = useState(1)

  const d3Zoom = zoom().scaleExtent(DEFAULT_SCALE_EXTENT).on('zoom', zoomed)

  const zoomIn = () => {
    // @ts-ignore
    select(svgMapRef.current).transition().duration(500).call(d3Zoom.scaleBy, 2)
  }

  const zoomOut = () => {
    select(svgMapRef.current)
      .transition()
      .duration(500)
      // @ts-ignore
      .call(d3Zoom.scaleBy, 0.5)
  }

  const resetZoom = () => {
    // @ts-ignore
    select(svgMapRef.current).call(d3Zoom.transform, zoomIdentity)
  }
  function zoomed(event: any) {
    const svg = select(svgMapRef.current)
    const svgContent = svg.select('.content')
    //const circleGroupContent = svg.select('.circle-group')
    const { transform } = event
    svgContent.attr('transform', transform)
    svgContent.attr('stroke-width', 1 / transform.k)
    // circleGroupContent.selectAll('circle').each(function () {
    //   const r = select(this).attr('data-r')
    //   select(this).attr('r', Number(r) / transform.k)
    // })

    setZoomLevel(transform.k)
  }

  useEffect(() => {
    const svg = select(svgMapRef.current)

    // zoom
    d3Zoom.translateExtent([
      [-mapWidth / 4, -mapHeight / 4],
      [mapWidth + mapWidth / 4, mapHeight + mapHeight / 4],
    ])
    svg
      // @ts-ignore
      .call(d3Zoom)
  }, [mapWidth, mapHeight])

  // const FORCEX = 2
  // const FORCEY = 8
  //const SCALE = 'scale(1,1)'

  const orgsByCity = Array.from(data)
    //filter out orgs with no city
    .filter(([key]) => Boolean(key))
    .map(([key, values]) => [
      key,
      values.map((value) => {
        //color for geo_scope:
        const color =
          display === 'orgs'
            ? value.geo_scope.includes('local')
              ? 'green'
              : value.geo_scope.includes('state')
              ? 'mediumyellow'
              : value.geo_scope.includes('regional')
              ? 'brown'
              : value.geo_scope.includes('national')
              ? 'red'
              : 'ink'
            : display === 'inits'
            ? value.initiative_type?.includes('government')
              ? 'green'
              : value.initiative_type?.includes('academic')
              ? 'mediumyellow'
              : value.initiative_type?.includes('faith_based')
              ? 'brown'
              : value.initiative_type?.includes('descendant')
              ? 'red'
              : 'ink'
            : display === 'all'
            ? value.initCommTask === true
              ? 'mediumyellow'
              : 'green'
            : 'ink'
        //give each org an initial x and y -- use latlong of first org in city just make sure all coords are same within city
        const projected = projection([values[0].long, values[0].lat])
        const x = projected ? projected[0] : 0
        const y = projected ? projected[1] : 0
        return { ...value, color, x, y }
      }),
    ])

  const orgNodes: OrganizationNode[] = orgsByCity
    .map((city) => city[1])
    .flat() as OrganizationNode[]

  const STRENGTH = -1 / zoomLevel
  const RADIUS = 6 / zoomLevel

  //const dots = select('.circle-group')
  const simulation = forceSimulation(orgNodes)
    .force('charge', forceManyBody().strength(STRENGTH))
    .force(
      'x',
      forceX().x((c) => (c as OrganizationNode).x)
    )
    .force(
      'y',
      forceY().y((c) => (c as OrganizationNode).y)
    )
    .force('collide', forceCollide().radius(RADIUS))
    .stop()

  // const g = dots
  //   .selectAll('g')
  //   .data(orgNodes)
  //   .join('g')
  //   // .attr('transform', (d: any) => `translate(${d.x},${d.y})`)
  //   .attr('class', 'org-circle')

  // g.append('circle')
  //   .attr('r', 6)
  //   .attr('class', (d: any) => d.color)
  //.attr('class', 'circle')

  //const circles = g.selectAll('circle')

  // const ticked = () => {
  //   //x.attr('transform', (d: any) => `translate(${d.x},${d.y})`)
  //   circles.attr('cx', (d: any) => d.x).attr('cy', (d: any) => d.y)
  // }
  // simulation.nodes(orgNodes).on('tick', ticked)
  simulation.tick(
    Math.ceil(
      Math.log(simulation.alphaMin()) / Math.log(1 - simulation.alphaDecay())
    )
  )

  //workaround for TailwindCSS suddenly not being able to see 'lightyellow' or  'yellow-map'
  const getColor = (color: string | undefined) => {
    const colors: { [key: string]: string } = {
      red: '#D93F27',
      blue: '#0C91A9',
      orange: '#DA9B28',
      lightyellow: '#CFC112',
      ink: '#212121',
      mediumyellow: '#F8CF40',
      green: '#629A41',
    }
    if (color) return colors[color] as string
    else return colors.ink
  }
  return (
    <div
      id="map-container"
      className="flex flex-grow min-h-[504px] w-full relative -mx-8"
      ref={hiddenMapRef}
    >
      <svg
        width={mapWidth}
        height={mapHeight}
        ref={svgMapRef}
        viewBox={`0 0 ${mapWidth} ${mapHeight}`}
      >
        <defs>
          <clipPath id="slippy-map">
            <rect x="0" y="0" width="100%" height="100%" />
          </clipPath>
        </defs>
        <g className="content" clipPath={`url(#slippy-map)`}>
          <path
            className="fill-white stroke-black line-join-round line-cap-round"
            d={`${path(feature(us, us.objects.nation))}`}
          ></path>
          <path
            className="fill-white stroke-black line-join-round line-cap-round"
            d={`${path(
              mesh(us, us.objects.states, function (a, b) {
                return a !== b
              })
            )}`}
          />
          <g className="circle-group">
            {orgNodes
              // .sort((a, b) => {
              //   return descending(a.length, b.length)
              // })
              .map((org: OrganizationNode, i: number) => {
                return (
                  <MapTooltip
                    key={`tooltip-for-${org.org_name}-${i}`}
                    organization={org}
                  >
                    <g
                      className="cursor-pointer city-circle"
                      onClick={() => handleOrganizationClick(org)}
                    >
                      <circle
                        className={`hover:stroke-ink fill-${org.color}`}
                        data-r={6 / zoomLevel} //rScale(city.length)}
                        //data-stroke-width={3}
                        style={{ fillOpacity: 1, fill: getColor(org.color) }}
                        r={6 / zoomLevel} //rScale(city.length)}
                        cx={org.x}
                        cy={org.y}
                      />
                      {/* <text
                        className="font-decimal text-black"
                        data-font-size={city.length > 1 ? 18 : 12}
                        style={{
                          alignmentBaseline: 'middle',
                          textAnchor: 'middle',
                        }}
                      >
                        {city.length}
                      </text> */}
                    </g>
                  </MapTooltip>
                )
              })}
          </g>
        </g>
      </svg>
      {children}
      <div className="absolute right-0 top-0 pr-4 pt-2 flex flex-col gap-2 items-center">
        <ZoomComponent label="+" onClick={zoomIn} />
        <ZoomComponent label="-" onClick={zoomOut} />
        <div
          className="bg-green rounded-[9px] w-[36px] h-[36px] text-white text-[26px] font-bold font-decimal flex items-center justify-center cursor-pointer user"
          onClick={resetZoom}
          role="button"
          style={{
            userSelect: 'none',
          }}
        >
          <img src={resetMapIcon} alt="reset map zoom" />
        </div>
      </div>
    </div>
  )
}

export default SlippyMap
